import { useEffect, useState } from 'react'
import { api, type ApiError } from '../lib/api'
import { useAuthStore } from '../lib/auth-store'
import { navigate } from '../lib/router'
import { AuthLayout } from '../components/auth/AuthLayout'
import { Button } from '../components/ui/button'
import { StatusBox } from '../components/ui/badge'

export function AcceptInvitePage() {
  const accessToken = useAuthStore((s) => s.accessToken)
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'login'>('loading')
  const [message, setMessage] = useState('')
  const [tenantName, setTenantName] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('token')

    if (!token) {
      setStatus('error')
      setMessage('Missing invite token.')
      return
    }

    if (!accessToken) {
      setStatus('login')
      setMessage(
        'Sign up or log in to accept this invite. The signup form will join the workspace as soon as you finish — no separate steps.',
      )
      return
    }

    api
      .acceptInvite(accessToken, token)
      .then((res) => {
        setStatus('success')
        setTenantName(res.tenantName)
        setMessage(`You've joined ${res.tenantName} as ${res.role}.`)
      })
      .catch((err: ApiError) => {
        setStatus('error')
        setMessage(err.title ?? 'Failed to accept invite')
      })
  }, [accessToken])

  const tone = status === 'success' ? 'success' : status === 'error' ? 'destructive' : 'volt'

  return (
    <AuthLayout
      title="Workspace invite"
      subtitle={status === 'success' && tenantName ? `Welcome to ${tenantName}.` : undefined}
      panel={{ line1: "You've been invited.", line2: 'Join the workspace and start building together.' }}
    >
      <div className="space-y-4">
        {message ? <StatusBox tone={tone}>{message}</StatusBox> : null}

        {status === 'success' && (
          <Button size="auth" onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </Button>
        )}
        {status === 'login' && (
          <div className="space-y-3">
            <Button
              size="auth"
              onClick={() => {
                const token = new URLSearchParams(window.location.search).get('token')
                navigate(`/signup${token ? `?token=${encodeURIComponent(token)}` : ''}`)
              }}
            >
              Create account & join
            </Button>
            <Button variant="ghost" size="auth" onClick={() => navigate('/login')}>
              I already have an account
            </Button>
          </div>
        )}
        {status === 'error' && (
          <Button variant="outline" size="auth" onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </Button>
        )}
      </div>
    </AuthLayout>
  )
}
